import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import {
  CreditCard as CardIcon,
  QrCode as QrIcon,
  AttachMoney as CashIcon,
} from '@mui/icons-material';
import { useSalesModal } from '../../../hooks/useSalesModal';
import { useOrdersPermissions } from '../../../hooks/useOrdersPermissions';
import { salesService } from '../../../services/sales.service';
import { CreateSalesOrderItemDto } from '../../../api';
import { selectCurrentUser } from '../../../redux/slices/auth.slice';
import { roundMoney } from '../../../utils/money';
import { buildCreateOrderPayload } from '../../../utils/sales/build-create-order-payload';
import { buildCommercialUpdatePayload } from '../../../utils/sales/build-commercial-update-payload';
import {
  cartHasRemoteItems,
  countRemoteFulfillmentGroups,
} from '../../../utils/sales/mixed-sale.utils';
import { isValid } from 'date-fns';
import { isCommercialDateChoiceComplete } from '../../../utils/sales/commercial-sale-date';
import { extractErrorMessage } from '../../../utils/errorHelpers';
import type { ZeroSaleAckKind } from '../../modals/confirmation';

// Opciones de métodos de pago para UI
const PAYMENT_METHOD_OPTIONS = [
  { id: 'CASH', name: 'Efectivo', icon: CashIcon, color: 'success.main' },
  { id: 'CARD', name: 'Tarjeta', icon: CardIcon, color: 'primary.main' },
  { id: 'QR', name: 'QR', icon: QrIcon, color: 'secondary.main' },
];

export interface SalesOrderCompletedPayload {
  orderCode: string;
  branchReceiptCode?: string | null;
  /** ISO 8601: fecha comercial de la venta (`saleDate` en servidor; puede diferir del momento de registro). */
  completedAt: string;
}

export interface UseSalesStep3Props {
  onOrderCompleted?: (payload: SalesOrderCompletedPayload) => void;
}

export const useSalesStep3 = ({ onOrderCompleted }: UseSalesStep3Props) => {
  const {
    state,
    orderSummary,
    setLoading,
    previousStep,
    finish,
    chargeCategories,
    setProductDelivered,
    applyDeliveryDefaults,
  } = useSalesModal();
  const currentUser = useSelector(selectCurrentUser);
  const { canMixedSell, canModifySaleDate, canEditSaleDate, canChangeSeller } =
    useOrdersPermissions();

  const [isProcessing, setIsProcessing] = useState(false);
  const processingRef = useRef(false);
  const [orderNumber, setOrderNumber] = useState('');
  const [branchReceiptNumber, setBranchReceiptNumber] = useState<string | null>(null);
  const [orderCompletedAt, setOrderCompletedAt] = useState<string | null>(null);
  const [showPrintModal, setShowPrintModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmError, setConfirmError] = useState<string | null>(null);
  const [saleDateModeError, setSaleDateModeError] = useState(false);
  const defaultsAppliedRef = useRef(false);

  const isEditMode = state.mode === 'edit';
  const requiresSaleDateChoice = isEditMode ? canEditSaleDate : canModifySaleDate;
  const sellerRequired = isEditMode || canChangeSeller;
  const sellingLocationId = state.selectedLocation?.id;

  // Apply delivery defaults once when entering confirmation (payment already known).
  useEffect(() => {
    if (state.mode === 'edit') {
      defaultsAppliedRef.current = true;
      return;
    }
    if (defaultsAppliedRef.current) return;
    defaultsAppliedRef.current = true;
    applyDeliveryDefaults(orderSummary.paymentStatus, sellingLocationId);
  }, [applyDeliveryDefaults, orderSummary.paymentStatus, sellingLocationId, state.mode]);

  const isMixedSale = useMemo(
    () => cartHasRemoteItems(state.selectedProducts, sellingLocationId),
    [state.selectedProducts, sellingLocationId],
  );

  const remoteGroupCount = useMemo(
    () => countRemoteFulfillmentGroups(state.selectedProducts, sellingLocationId),
    [state.selectedProducts, sellingLocationId],
  );

  const canConfirmMixedSale = !isMixedSale || canMixedSell;

  // Required choices still pending: keep the confirm button disabled until both are set.
  const missingSeller = sellerRequired && state.selectedSeller == null;
  const missingSaleDate =
    requiresSaleDateChoice && !isCommercialDateChoiceComplete(state.saleDateMode, state.saleDate);

  // Detect products with loss
  const lossItems = useMemo(
    () =>
      state.selectedProducts
        .map((product) => {
          const cost = roundMoney(product.warehouseInventoryProduct.costPrice || 0);
          const final = roundMoney(product.finalPrice);
          const loss = roundMoney(cost - final);
          return {
            code: product.warehouseInventoryProduct.productCodeUnique,
            name: product.warehouseInventoryProduct.product.name,
            loss,
          };
        })
        .filter((p) => p.loss > 0),
    [state.selectedProducts],
  );

  const getDiscountType = useCallback(
    (discountType: string): CreateSalesOrderItemDto.discountType => {
      switch (discountType) {
        case 'PERCENTAGE':
          return CreateSalesOrderItemDto.discountType.PERCENTAGE;
        case 'AMOUNT':
          return CreateSalesOrderItemDto.discountType.AMOUNT;
        default:
          return CreateSalesOrderItemDto.discountType.NONE;
      }
    },
    [],
  );

  const getMarkupType = useCallback((markupType: string): CreateSalesOrderItemDto.markupType => {
    switch (markupType) {
      case 'PERCENTAGE':
        return CreateSalesOrderItemDto.markupType.PERCENTAGE;
      case 'AMOUNT':
        return CreateSalesOrderItemDto.markupType.AMOUNT;
      default:
        return CreateSalesOrderItemDto.markupType.NONE;
    }
  }, []);

  const openConfirmModal = useCallback(() => {
    if (!isEditMode && !canConfirmMixedSale) {
      setConfirmError(
        'No tiene permiso para confirmar ventas con piezas de otras sucursales o almacén (orders.sales-management.mixed_sell).',
      );
      return;
    }
    if (sellerRequired && state.selectedSeller == null) {
      setConfirmError('Seleccione un vendedor para continuar.');
      return;
    }
    if (requiresSaleDateChoice && state.saleDateMode == null) {
      setSaleDateModeError(true);
      return;
    }
    if (state.saleDateMode === 'custom' && !isValid(state.saleDate)) {
      setConfirmError('Seleccione una fecha y hora de venta válida.');
      return;
    }
    if (isEditMode && orderSummary.grandTotal + 0.01 < state.originalAmountPaid) {
      setConfirmError(
        `El total no puede ser menor a lo ya pagado (${state.originalAmountPaid.toFixed(2)}).`,
      );
      return;
    }
    setSaleDateModeError(false);
    setConfirmError(null);
    setShowConfirmModal(true);
  }, [
    canConfirmMixedSale,
    isEditMode,
    orderSummary.grandTotal,
    requiresSaleDateChoice,
    sellerRequired,
    state.originalAmountPaid,
    state.saleDate,
    state.saleDateMode,
    state.selectedSeller,
  ]);

  const clearSaleDateModeError = useCallback(() => {
    setSaleDateModeError(false);
  }, []);

  const clearConfirmError = useCallback(() => {
    setConfirmError(null);
  }, []);

  const closeConfirmModal = useCallback(() => {
    if (isProcessing) return;
    setShowConfirmModal(false);
  }, [isProcessing]);

  const isFullZeroSale = useMemo(
    () => roundMoney(Number(orderSummary.grandTotal)) === 0,
    [orderSummary.grandTotal],
  );

  const requiresZeroSaleAck = useMemo(() => {
    const hasZeroLine = state.selectedProducts.some((p) => roundMoney(Number(p.finalPrice)) === 0);
    return hasZeroLine || isFullZeroSale;
  }, [state.selectedProducts, isFullZeroSale]);

  const zeroSaleAckKind: ZeroSaleAckKind = isFullZeroSale ? 'full' : 'partial';

  // Handle confirm sale
  const handleConfirmSale = useCallback(async () => {
    if (processingRef.current || isProcessing) {
      return;
    }

    if (!isEditMode && !canConfirmMixedSale) {
      setConfirmError(
        'No tiene permiso para confirmar ventas con piezas de otras sucursales o almacén (orders.sales-management.mixed_sell).',
      );
      setShowConfirmModal(false);
      return;
    }

    if (sellerRequired && state.selectedSeller == null) {
      setConfirmError('Seleccione un vendedor para continuar.');
      setShowConfirmModal(false);
      return;
    }

    if (requiresSaleDateChoice && state.saleDateMode == null) {
      setSaleDateModeError(true);
      setShowConfirmModal(false);
      return;
    }

    if (state.saleDateMode === 'custom' && !isValid(state.saleDate)) {
      setConfirmError('Seleccione una fecha y hora de venta válida.');
      setShowConfirmModal(false);
      return;
    }

    if (isEditMode && orderSummary.grandTotal + 0.01 < state.originalAmountPaid) {
      setConfirmError(
        `El total no puede ser menor a lo ya pagado (${state.originalAmountPaid.toFixed(2)}).`,
      );
      setShowConfirmModal(false);
      return;
    }

    processingRef.current = true;
    setConfirmError(null);
    setIsProcessing(true);
    setLoading(true);

    try {
      if (isEditMode) {
        if (state.editingOrderId == null) {
          throw new Error('No hay una venta en edición');
        }
        const payload = buildCommercialUpdatePayload(state);
        if (requiresZeroSaleAck) {
          payload.confirmZeroSale = true;
        }
        const updated = await salesService.updateSalesOrderCommercial(
          state.editingOrderId,
          payload,
        );
        setOrderNumber(updated.orderCode);
        setBranchReceiptNumber(updated.branchReceiptCode ?? null);
        const completedAtIso = updated.saleDate.toISOString();
        setOrderCompletedAt(completedAtIso);
        setShowConfirmModal(false);
        if (onOrderCompleted) {
          onOrderCompleted({
            orderCode: updated.orderCode,
            branchReceiptCode: updated.branchReceiptCode ?? null,
            completedAt: completedAtIso,
          });
        }
        return;
      }

      const orderData = buildCreateOrderPayload({
        state,
        orderSummary,
        currentUserId: currentUser?.id,
        getDiscountType,
        getMarkupType,
        confirmZeroSale: requiresZeroSaleAck,
      });

      const newOrder = isMixedSale
        ? await salesService.createMixedSale(orderData)
        : await salesService.createSalesOrder(orderData);

      setOrderNumber(newOrder.orderCode);
      setBranchReceiptNumber(newOrder.branchReceiptCode ?? null);
      const completedAtIso = newOrder.saleDate.toISOString();
      setOrderCompletedAt(completedAtIso);
      setShowConfirmModal(false);

      if (onOrderCompleted) {
        onOrderCompleted({
          orderCode: newOrder.orderCode,
          branchReceiptCode: newOrder.branchReceiptCode ?? null,
          completedAt: completedAtIso,
        });
      }
    } catch (error) {
      console.error('Error al procesar la venta:', error);
      setShowConfirmModal(false);

      setConfirmError(
        extractErrorMessage(error, 'No se pudo confirmar la venta. Intente de nuevo.'),
      );
    } finally {
      processingRef.current = false;
      setIsProcessing(false);
      setLoading(false);
    }
  }, [
    canConfirmMixedSale,
    isEditMode,
    isProcessing,
    requiresSaleDateChoice,
    sellerRequired,
    state,
    orderSummary,
    currentUser,
    onOrderCompleted,
    setLoading,
    getDiscountType,
    getMarkupType,
    isMixedSale,
    requiresZeroSaleAck,
  ]);

  // Handle print receipt
  const handlePrintReceipt = useCallback(() => {
    setShowPrintModal(true);
  }, []);

  // Handle finish
  const handleFinish = useCallback(() => {
    setIsProcessing(false);
    setLoading(false);
    finish();
  }, [finish, setLoading]);

  // Handle close print modal
  const handleClosePrintModal = useCallback(() => {
    setShowPrintModal(false);
    handleFinish();
  }, [handleFinish]);

  return {
    // State
    state,
    orderSummary,
    isProcessing,
    orderNumber,
    branchReceiptNumber,
    orderCompletedAt,
    showPrintModal,
    showConfirmModal,
    requiresZeroSaleAck,
    zeroSaleAckKind,
    isFullZeroSale,
    lossItems,
    paymentMethods: PAYMENT_METHOD_OPTIONS,
    chargeCategories,
    isMixedSale,
    remoteGroupCount,
    canConfirmMixedSale,
    confirmError,
    saleDateModeError,
    sellingLocationId,
    isEditMode,
    sellerRequired,
    missingSeller,
    missingSaleDate,
    // Handlers
    openConfirmModal,
    closeConfirmModal,
    clearSaleDateModeError,
    clearConfirmError,
    handleConfirmSale,
    handlePrintReceipt,
    handleFinish,
    handleClosePrintModal,
    previousStep,
    setProductDelivered,
  };
};
